import { ThunkDispatch } from "core-integration/src/store";
import {
  cancelProposedChip,
  proposeAvailableChip,
} from "core-integration/src/store/chips/actions";
import {
  getActiveChipName,
  getPotentialChips,
  getProposedChipName,
} from "core-integration/src/store/chips/reducers";
import { getElementTypesBySquadPosition } from "core-integration/src/store/element-types/reducers";
import { IElementTypesBySquadPosition } from "core-integration/src/store/element-types/types";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import {
  getErrors,
  getFreeTransfersRemaining,
  getProposedElements,
  getToSpend,
  getTransferCosts,
} from "core-integration/src/store/squad/reducers";
import { IProposedElements } from "core-integration/src/store/squad/types";
import {
  formatRawAsISO,
  formatRawAsLocalI18n,
} from "core-integration/src/utils/datetime";
import { integerToMoney } from "core-integration/src/utils/money";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "rebass/styled-components";
import styled, { css } from "styled-components";
import i18n, { dateLocales } from "../../i18n";
import { ChipName, getChipName } from "../../utils/chips";
import { integerToDecimal } from "../../utils/decimal";
import Alert from "../Alert";
import Button from "../Button";
import DeadlineBar from "../DeadlineBar";
import Dialog from "../Dialog";
import DialogManager from "../DialogManager";
import Link from "../Link";
import { BoxWrapStyles } from "../Utils";
import { ScoreboardPanel } from "./ScoreboardPanel";

const AlertLink = styled(Link)`
  margin-left: 3.8px;
  color: white;
`;

const DeadlineBarScoreboardWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.space[3]};
  margin: 1rem 1rem ${(props) => props.theme.space[4]};
  border-radius: ${({ theme }) => theme.radii[2]};

  @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
    padding: ${(props) => props.theme.space[2]};
    ${BoxWrapStyles};
  }

  @media (min-width: ${(props) => props.theme.breakpoints[3]}) {
    margin: 0;
  }
`;

const DeadlineBarWrap = styled.div`
  border-top: ${(props) => props.theme.borders[2]};

  @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
    border-top: 0;
  }
`;

const StyledBasicScoreboard = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  gap: ${(props) => props.theme.space[2]};
`;

const ScoreBoardInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${(props) => props.theme.space[2]};

  @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
    flex-direction: row;
    padding-inline-end: ${(props) => props.theme.space[2]};
  }
`;

const ScoreboardPanelWrap = styled.div`
  flex: 1;

  @media (min-width: ${(props) => props.theme.breakpoints[4]}) {
    display: flex;
    flex-direction: column;
  }
`;

interface IBasicProps {
  elementTypesByPosition: IElementTypesBySquadPosition;
  isBudgetExceeded: boolean;
  isNeedElements: boolean;
  proposedElements: IProposedElements;
}

const Basic = ({
  elementTypesByPosition,
  isBudgetExceeded,
  proposedElements,
}: IBasicProps) => {
  const { t } = useTranslation();

  const currencyDivisor = 10;
  const toSpend = useSelector(getToSpend);
  const nextEvent = useSelector(getNextEvent);

  //PRE ROLLOVER

  return (
    <>
      {!nextEvent && (
        <Box mb={4}>
          <Alert type="info">
            <Trans i18nKey="postSeason.seasonOver3">
              The NBA Fantasy season is now over. Follow the NBA Playoffs on
              <AlertLink
                to="https://www.nba.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                nba.com
              </AlertLink>
            </Trans>
          </Alert>
        </Box>
      )}
      <DeadlineBarScoreboardWrap>
        {nextEvent && (
          <DeadlineBarWrap>
            <DeadlineBar
              deadlineISO={formatRawAsISO(nextEvent.deadline_time)}
              deadlineLocal={formatRawAsLocalI18n(
                nextEvent.deadline_time,
                dateLocales[i18n.language],
              )}
              headingText={t(
                "createSquad.eventDeadline",
                "{{ eventName }} deadline",
                {
                  eventName: nextEvent.name,
                },
              )}
            />
          </DeadlineBarWrap>
        )}

        <StyledBasicScoreboard>
          <ScoreBoardInfo>
            <ScoreboardPanelWrap>
              <ScoreboardPanel
                heading={t("scoreboard.playersSelected", "Players Selected")}
                value={`${Object.keys(proposedElements).length} / ${
                  Object.keys(elementTypesByPosition).length
                }`}
              />
            </ScoreboardPanelWrap>

            <ScoreboardPanelWrap>
              <ScoreboardPanel
                heading={t("scoreboard.moneyRemaining", "Money Remaining")}
                value={`${integerToMoney(toSpend, currencyDivisor)}`}
                isError={isBudgetExceeded}
                isSuccess={!isBudgetExceeded}
              />
            </ScoreboardPanelWrap>
          </ScoreBoardInfo>
        </StyledBasicScoreboard>
      </DeadlineBarScoreboardWrap>
    </>
  );
};

//PRE ROLLOVER

interface IExplainChipDialogProps {
  chip: string;
  handleHide: () => void;
  transferCosts: number;
}

const ExplainChipDialog = ({
  chip,
  handleHide,
  transferCosts,
}: IExplainChipDialogProps) => {
  const { t } = useTranslation();
  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        {chip === "wildcard" && t("squad.playWildcardText", "Play Wildcard")}
        {chip === "rich" && t("squad.playAllStarText", "Play All-Star")}
      </Dialog.Header>
      <Dialog.Body isPadded={true}>
        <Alert type="info">
          {transferCosts ? (
            <>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText1",
                    "You are now proposing to play your wildcard. Unlimited transactions can be made this Gameday without points deduction.",
                  )}
                {chip === "rich" &&
                  t(
                    "squad.allStarext1",
                    "You are now proposing to play your All-Star chip. Unlimited transactions with an unlimted budget can be made this Gameday without points deduction.",
                  )}
              </p>
              <p>
                {chip === "wildcard" &&
                  t(
                    "squad.wildcardText2",
                    "Your wildcard will not be activated until you confirm your transactions.",
                  )}
                {chip === "rich" &&
                  t(
                    "squad.allStarText2",
                    "Your All-Star will not be activated until you confirm your transactions",
                  )}
              </p>
            </>
          ) : (
            <p>
              {chip === "wildcard" &&
                t(
                  "squad.wildcardText3",
                  "To activate your wildcard you must be making enough proposed transactions that points will be deducted.",
                )}
              {chip === "rich" &&
                t(
                  "squad.allStarText3",
                  "To activate your All-Star chip you must be making enough proposed transactions that points will be deducted.",
                )}
            </p>
          )}
        </Alert>
      </Dialog.Body>
    </Dialog>
  );
};

interface ICostProps {
  isBudgetExceeded: boolean;
}

const Cost = ({ isBudgetExceeded }: ICostProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<ThunkDispatch>();

  const nextEvent = useSelector(getNextEvent);
  const activeChipName = useSelector(getActiveChipName);
  const currencyDivisor = 10;
  const freeTransfersRemaining = useSelector(getFreeTransfersRemaining);
  const proposedChipName = useSelector(getProposedChipName);
  const toSpend = useSelector(getToSpend);
  const transferCosts = useSelector(getTransferCosts);
  const chips = useSelector(getPotentialChips);
  const wildCardChip = chips.filter((c) => c.name === "wildcard")[0];
  const AllStarChip = chips.filter((c) => c.name === "rich")[0];

  const proposeChip = (chip: string) => dispatch(proposeAvailableChip(chip));
  const cancelChip = (chip: string) => dispatch(cancelProposedChip(chip));

  //POST ROLLOVER

  const RolloverScoreboardWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.space[3]};
    margin: 0 ${(props) => props.theme.space[2]};

    @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
      padding: ${(props) => props.theme.space[3]};
      ${BoxWrapStyles}
    }

    @media (min-width: ${(props) => props.theme.breakpoints[3]}) {
      margin: 0;
    }
  `;

  const DeadlineBarWrap = styled.div`
    border-top: ${(props) => props.theme.borders[2]};

    @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
      border-top: 0;
    }
  `;

  const InfoChipWrap = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: ${(props) => props.theme.space[3]};

    @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
      order: 1;
      flex-direction: column;
    }
  `;

  const InfoWrap = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.space[2]};

    @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
      order: 1;
      flex-direction: row;
      justify-content: space-between;
    }
  `;

  const ChipButtonWrap = styled.div`
    flex: 0.7;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${(props) => props.theme.space[3]};
    ${
      activeChipName &&
      css`
        gap: 0;
      `
    }

    @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
      order: 1;
      flex: 1;
      flex-direction: row;
    }
  `;

  const ChipButtonWrapMob = styled.div`
    flex: 0.7;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${(props) => props.theme.space[3]};

    @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
      display: none;
    }
  `;

  const TransactionActionChipWrapWide = styled.div`
    display: none;

    @media (min-width: ${(props) => props.theme.breakpoints[1]}) {
      display: flex;
      justify-content: space-between;
      gap: ${(props) => props.theme.space[3]};
    }
  `;

  const StyledButton = styled(Button)`
    flex: 1;
  `;

  return (
    <RolloverScoreboardWrap>
      {nextEvent && (
        <DeadlineBarWrap>
          <DeadlineBar
            deadlineISO={formatRawAsISO(nextEvent.deadline_time)}
            deadlineLocal={formatRawAsLocalI18n(
              nextEvent.deadline_time,
              dateLocales[i18n.language],
            )}
            headingText={t(
              "createSquad.eventDeadline",
              "{{ eventName }} deadline",
              {
                eventName: nextEvent.name,
              },
            )}
          />
        </DeadlineBarWrap>
      )}

      <InfoChipWrap>
        <InfoWrap>
          <ScoreboardPanel
            heading={t("squad.freeTransfers", "Free Transactions")}
            value={
              activeChipName === "wildcard" || activeChipName === "rich"
                ? t("squad.unlimited", "Unlimited")
                : `${freeTransfersRemaining}`
            }
          />

          <ScoreboardPanel
            heading={t("squad.cost", "Cost")}
            value={`${integerToDecimal(transferCosts, 10)} pts`}
            isError={transferCosts > 0}
          />

          <ScoreboardPanel
            heading={t("squad.moneyRemaining", "Money Remaining")}
            value={
              proposedChipName === "rich" || activeChipName === "rich"
                ? `--`
                : `${integerToMoney(toSpend, currencyDivisor)}`
            }
            isError={isBudgetExceeded}
            isSuccess={!isBudgetExceeded}
          />
        </InfoWrap>
        <ChipButtonWrapMob>
          {activeChipName ? (
            <ScoreboardPanel
              heading={t("scoreboard.chip.active", "Active Chip")}
              value={getChipName(activeChipName as ChipName, t)}
            />
          ) : wildCardChip.status_for_entry === "played" ? (
            <ScoreboardPanel
              heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
              value={t("scoreboard.wildcard.played", "Played")}
            />
          ) : wildCardChip.status_for_entry === "active" ? (
            <ScoreboardPanel
              heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
              value={t("scoreboard.wildcard.active", "Active")}
            />
          ) : proposedChipName === "wildcard" ? (
            <StyledButton onClick={() => cancelChip("wildcard")}>
              {t("squad.cancelWildcardText", "Cancel Wildcard")}
            </StyledButton>
          ) : (
            <DialogManager
              render={(showDialog, handleShow, handleHide) => (
                <>
                  <StyledButton
                    disabled={
                      proposedChipName === "rich" || activeChipName === "rich"
                    }
                    onClick={handleShow}
                    fullwidth="true"
                  >
                    {t("squad.playWildcardTest", "Play Wildcard")}
                  </StyledButton>

                  {showDialog && (
                    <ExplainChipDialog
                      chip="wildcard"
                      handleHide={() => {
                        handleHide();
                        if (transferCosts) {
                          proposeChip("wildcard");
                        }
                      }}
                      transferCosts={transferCosts}
                    />
                  )}
                </>
              )}
            />
          )}
          {activeChipName ? (
            <>&nbsp;</>
          ) : AllStarChip.status_for_entry === "played" ? (
            <ScoreboardPanel
              heading={t("scoreboard.allstar.allstar", "All-Star")}
              value={t("scoreboard.allstar.played", "Played")}
            />
          ) : AllStarChip.status_for_entry === "active" ? (
            <ScoreboardPanel
              heading={t("scoreboard.allstar.allstar", "All-Star")}
              value={t("scoreboard.allstar.active", "Active")}
            />
          ) : proposedChipName === "rich" ? (
            <StyledButton onClick={() => cancelChip("rich")}>
              {t("squad.cancelAllStarText", "Cancel All-Star")}
            </StyledButton>
          ) : (
            <DialogManager
              render={(showDialog, handleShow, handleHide) => (
                <>
                  <StyledButton
                    disabled={
                      proposedChipName === "wildcard" ||
                      activeChipName === "wildcard"
                    }
                    onClick={handleShow}
                    fullwidth="true"
                  >
                    {t("squad.playAllStarText", "Play All-Star")}
                  </StyledButton>

                  {showDialog && (
                    <ExplainChipDialog
                      chip="rich"
                      handleHide={() => {
                        handleHide();
                        if (transferCosts) {
                          proposeChip("rich");
                        }
                      }}
                      transferCosts={transferCosts}
                    />
                  )}
                </>
              )}
            />
          )}
        </ChipButtonWrapMob>
        <TransactionActionChipWrapWide>
          <ChipButtonWrap>
            {activeChipName ? (
              <ScoreboardPanel
                heading={t("scoreboard.chip.active", "Active Chip")}
                value={getChipName(activeChipName as ChipName, t)}
              />
            ) : wildCardChip.status_for_entry === "played" ? (
              <ScoreboardPanel
                heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
                value={t("scoreboard.wildcard.played", "Played")}
              />
            ) : wildCardChip.status_for_entry === "active" ? (
              <ScoreboardPanel
                heading={t("scoreboard.wildcard.wildcard", "Wildcard")}
                value={t("scoreboard.wildcard.active", "Active")}
              />
            ) : proposedChipName === "wildcard" ? (
              <StyledButton onClick={() => cancelChip("wildcard")}>
                {t("squad.cancelWildcardText", "Cancel Wildcard")}
              </StyledButton>
            ) : (
              <DialogManager
                render={(showDialog, handleShow, handleHide) => (
                  <>
                    <StyledButton
                      disabled={
                        proposedChipName === "rich" || activeChipName === "rich"
                      }
                      onClick={handleShow}
                      fullwidth="true"
                    >
                      {t("squad.playWildcardTest", "Play Wildcard")}
                    </StyledButton>

                    {showDialog && (
                      <ExplainChipDialog
                        chip="wildcard"
                        handleHide={() => {
                          handleHide();
                          if (transferCosts) {
                            proposeChip("wildcard");
                          }
                        }}
                        transferCosts={transferCosts}
                      />
                    )}
                  </>
                )}
              />
            )}
            {activeChipName ? (
              <>&nbsp;</>
            ) : AllStarChip.status_for_entry === "played" ? (
              <ScoreboardPanel
                heading={t("scoreboard.allstar.allstar", "All-Star")}
                value={t("scoreboard.allstar.played", "Played")}
              />
            ) : AllStarChip.status_for_entry === "active" ? (
              <ScoreboardPanel
                heading={t("scoreboard.allstar.allstar", "All-Star")}
                value={t("scoreboard.allstar.active", "Active")}
              />
            ) : proposedChipName === "rich" ? (
              <StyledButton onClick={() => cancelChip("rich")}>
                {t("squad.cancelAllStarText", "Cancel All-Star")}
              </StyledButton>
            ) : (
              <DialogManager
                render={(showDialog, handleShow, handleHide) => (
                  <>
                    <StyledButton
                      disabled={
                        proposedChipName === "wildcard" ||
                        activeChipName === "wildcard"
                      }
                      onClick={handleShow}
                      fullwidth="true"
                    >
                      {t("squad.playAllStarText", "Play All-Star")}
                    </StyledButton>

                    {showDialog && (
                      <ExplainChipDialog
                        chip="rich"
                        handleHide={() => {
                          handleHide();
                          if (transferCosts) {
                            proposeChip("rich");
                          }
                        }}
                        transferCosts={transferCosts}
                      />
                    )}
                  </>
                )}
              />
            )}
          </ChipButtonWrap>
        </TransactionActionChipWrapWide>
      </InfoChipWrap>
    </RolloverScoreboardWrap>
  );
};

//POST ROLLOVER

interface IProps {
  scoreboard: string;
}

const Scoreboard = ({ scoreboard }: IProps) => {
  const proposedElements = useSelector(getProposedElements);
  const elementTypesByPosition = useSelector(getElementTypesBySquadPosition);
  const errors = useSelector(getErrors);
  const isNeedElements = Boolean(errors.needElements);
  const isBudgetExceeded = Boolean(errors.budgetExceeded);
  if (!elementTypesByPosition) {
    return null;
  }
  switch (scoreboard) {
    case "basic":
      return (
        <Basic
          elementTypesByPosition={elementTypesByPosition}
          isNeedElements={isNeedElements}
          isBudgetExceeded={isBudgetExceeded}
          proposedElements={proposedElements}
        />
      );
    case "cost":
      return <Cost isBudgetExceeded={isBudgetExceeded} />;
    default:
      return null;
  }
};

export default Scoreboard;
